import { CoursesIcon, Table, useDefaultSorting } from '@bp/ui-components';
import { Row } from '@tanstack/react-table';
import { BpLink } from '../BpLink/BpLink';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { GroupSubject } from '@bp/pim-auth-permissions';
import { FragmentType, getFragmentData, graphql } from 'client/gql';
import { useMatrixNotifications } from 'hooks/matrix/useMatrixNotifications';

const CoursesPreviewTableFieldsDocument = graphql(`
  fragment CoursesPreviewTableFields on Query {
    groups(
      where: {
        type: course
        organization: { uuid_IN: $organizations }
        AND: [
          { OR: [{ editors_SOME: { uuid: $profile } }, { viewers_SOME: { uuid: $profile } }] }
          { OR: [{ inCurrentSchoolyear: true }, { isCommunity: true }] }
        ]
      }
    ) {
      uuid
      __typename
      isCommunity
      organization {
        uuid
        __typename
      }
      events(where: { virtualLocations_SOME: { running: true } }) {
        uuid
        __typename
      }
      name
      schoolYear {
        uuid
        __typename
      }
      teachingUnitsConnection {
        __typename
        edges {
          __typename
          node {
            uuid
            __typename
            assignments {
              uuid
              submissions(where: { status: New }) {
                uuid
                __typename
              }
            }
          }
        }
      }
    }
    assignments(where: { submissions_SOME: { owner: { uuid: $profile } } }) {
      uuid
      __typename
      submissions(where: $submissionsWhere) {
        uuid
        __typename
      }
      holder {
        ... on TeachingUnit {
          uuid
          __typename
          group {
            uuid
          }
        }
      }
    }
  }
`);

export type CoursesPreviewTableType = {
  uuid: string;
  organization: { uuid: string };
  name: string;
  meetingInProgress: boolean;
  count?: number;
  newMaterial: boolean;
};

type CoursesPreviewTableProps = {
  query: FragmentType<typeof CoursesPreviewTableFieldsDocument> | undefined;
  onNavigate: (uuid: string) => void;
  isCourseEditor: (group: GroupSubject) => boolean | undefined;
};

export const CoursesPreviewTable: FC<CoursesPreviewTableProps> = ({ query, onNavigate, isCourseEditor }) => {
  const { t } = useTranslation();
  const { sorting, onSortingChange } = useDefaultSorting();
  const fragment = getFragmentData(CoursesPreviewTableFieldsDocument, query);
  const groups = fragment?.groups;
  const assignments = fragment?.assignments;

  const matrixNotifications = useMatrixNotifications(groups?.map((g) => g.uuid) ?? []);

  const data: CoursesPreviewTableType[] =
    groups?.map((course) => {
      const notification = matrixNotifications.get(course.uuid);
      let newMessages = notification?.hasNewMessages ?? false;

      if (!newMessages) {
        // Check submissions notifications
        newMessages =
          assignments
            ?.filter((assignment) => assignment?.holder?.group?.uuid === course.uuid)
            ?.some((assignment) => {
              return assignment.submissions?.some((submission) => {
                const submissionNotification = matrixNotifications.get(submission.uuid);
                return submissionNotification?.hasNewMessages ?? false;
              });
            }) ?? false;
      }

      return {
        uuid: course.uuid,
        organization: course.organization,
        name: course.name,
        meetingInProgress: course.events.length > 0,
        count: notification?.unreadCount ?? 0,
        newMaterial: newMessages,
      };
    }) ?? [];

  const dataWithSubmissionCount: CoursesPreviewTableType[] = data.map((course) => {
    const courseWithNewSubmissions = groups
      ?.map((a) => {
        const todoCount = a.teachingUnitsConnection.edges.flatMap((e) => {
          const newSubmsns = e.node.assignments.map((a) => a.submissions.length);
          return newSubmsns.reduce((a, b) => a + b, 0);
        });
        return { courseUuid: a.uuid, count: todoCount.reduce((a, b) => a + b, 0) };
      })
      .find((g) => g.courseUuid === course.uuid);
    return { ...course, count: courseWithNewSubmissions?.count ?? 0 };
  });

  return (
    <Table<CoursesPreviewTableType>
      hideHeader
      showSort
      sorting={sorting}
      onSortingChange={onSortingChange}
      customPadding='var(--spacing-6)'
      customRowHeight='var(--list-row-height)'
      customLastColSpacing='0'
      breakpoint={null}
      data={dataWithSubmissionCount}
      columns={[
        {
          id: 'name',
          accessorKey: 'name',
          cell: ({ row }: { row: Row<CoursesPreviewTableType> }) => (
            <BpLink
              value={row.original.name}
              onNavigate={() => onNavigate(row.original.uuid)}
              inProgress={row.original.meetingInProgress}
              inProgressHint={t('groups.activeMeetingHint')}
              counter={row.original.count}
              counterHint={t('groups.newSubmissionsHint')}
              hasNews={row.original.newMaterial}
              hasNewsHint={t('groups.newMessageHint')}
              isEditorOfCourse={isCourseEditor(row.original) ?? false}
            />
          ),
          canExpand: true,
        },
      ]}
      emptyStateSettings={{
        icon: <CoursesIcon className='svg-icon primary-light' />,
        size: 'small',
        title: '',
        subtitle: t('courses.noCourses'),
        padding: 'xl',
      }}
    />
  );
};
